import mapboxgl from 'mapbox-gl'
import Settings from '../settings'
import { LocationData } from '~/models/Destination'

export class GlobeService {
	private map: mapboxgl.Map
	private readonly countrySource: string = 'country-boundaries'
	private readonly countrySourceLayer: string = 'country_boundaries'

	private selectedCountry: string = ''

	constructor(globeId: string, language: string, coordinates: LocationData, zoom: number) {
		this.map = this.createMap(globeId, language, coordinates, zoom)
	}

	public async initialize(): Promise<GlobeService> {
		await new Promise((resolve) => {
			this.map.on('load', resolve)
		})
		this.addCountriesSource()
		this.addCountriesClick()
		return this
	}

	public onCountryClick(callback: (iso: string) => void): void {
		this.map.on('click', 'countries-click', (e) => {
			const feature = e.features?.[0]
			if (!feature) return
			const iso = feature.properties?.iso_3166_1

			callback(iso)

			if (this.selectedCountry && this.selectedCountry !== feature.id) {
				this.setSelected(this.selectedCountry, false)
				this.setHoverStatus(this.selectedCountry, false)
			}

			this.selectedCountry = feature.id as string
			this.setSelected(this.selectedCountry, true)
			this.setHoverStatus(this.selectedCountry, true)
		})
	}

	public addCountriesArea(layerId: string, color: string): void {
		this.map.addLayer({
			'id': layerId,
			'type': 'fill',
			'source': this.countrySource,
			'source-layer': 'country_boundaries',
			'paint': {
				'fill-color': color,
				'fill-opacity': 0.4,
			},
		})

		this.map.addLayer({
			'id': `${layerId}-borders`,
			'type': 'line',
			'source': this.countrySource,
			'source-layer': 'country_boundaries',
			'paint': {
				'line-color': color,
				'line-width': 1,
			},
		})
	}

	public updateCountriesArea(layerId: string, countries: string[]): void {
		const borderLayerId = `${layerId}-borders`
		const filter = [...this.politicFilters(), ['in', ['get', 'iso_3166_1'], ['literal', countries]]]

		if (this.map.getLayer(layerId)) this.map.setFilter(layerId, filter)
		if (this.map.getLayer(borderLayerId)) this.map.setFilter(borderLayerId, filter)
	}

	public deselectCountry() {
		this.setSelected(this.selectedCountry, false)
		this.setHoverStatus(this.selectedCountry, false)
	}

	private createMap(globeId: string, language: string, coordinates: LocationData, zoom: number) {
		mapboxgl.accessToken = Settings.mapboxToken()
		return new mapboxgl.Map({
			container: globeId,
			style: Settings.mapboxStylesUrl(),
			projection: 'globe',
			center: [coordinates.longitude, coordinates.latitude],
			zoom,
			language,
		})
	}

	private addCountriesSource(): void {
		this.map.addSource(this.countrySource, {
			type: 'vector',
			url: 'mapbox://mapbox.country-boundaries-v1',
		})
	}

	private addCountriesClick(): void {
		const layerId = 'countries-click'

		this.addCountriesClickLayer(layerId)
		this.addHoverToLayer(layerId)
		this.addCountriesClickLineLayer()
		this.setCursorHover(layerId)
	}

	private addCountriesClickLayer(layerId: string): void {
		this.map.addLayer({
			'id': layerId,
			'type': 'fill',
			'source': this.countrySource,
			'source-layer': this.countrySourceLayer,
			'paint': {
				'fill-opacity': 0,
				'fill-color': '#000000',
			},
			'filter': this.politicFilters(),
		})

		this.map.setPaintProperty(layerId, 'fill-opacity', [
			'case',
			['boolean', ['feature-state', 'hover'], false],
			0.4,
			0,
		])
	}

	private addCountriesClickLineLayer(): void {
		const layerId = 'countries-click-line'

		this.map.addLayer({
			'id': layerId,
			'type': 'line',
			'source': this.countrySource,
			'source-layer': this.countrySourceLayer,
			'paint': {
				'line-color': '#000000',
				'line-opacity': 0,
			},
			'filter': this.politicFilters(),
		})

		this.map.setPaintProperty(layerId, 'line-opacity', [
			'case',
			['boolean', ['feature-state', 'selected'], false],
			0.9,
			0,
		])
	}

	private addHoverToLayer(layerId: string): void {
		let currentId: string

		this.map.on('mousemove', layerId, (e) => {
			const featureId = e.features?.[0]?.id as string
			if (!featureId) return
			const isSelected = currentId === this.selectedCountry
			const isCurrent = currentId === featureId
			if (currentId && !isCurrent && !isSelected) this.setHoverStatus(currentId, false)
			currentId = featureId
			this.setHoverStatus(currentId, true)
		})

		this.map.on('mouseleave', layerId, () => {
			if (!currentId || this.selectedCountry === currentId) return
			this.setHoverStatus(currentId, false)
		})
	}

	private setCursorHover(layerId: string) {
		this.map.on('mouseenter', layerId, () => (this.map.getCanvas().style.cursor = 'pointer'))
		this.map.on('mouseleave', layerId, () => (this.map.getCanvas().style.cursor = 'default'))
	}

	private setHoverStatus(featureId: string, hover: boolean): void {
		this.map.setFeatureState(
			{
				id: featureId,
				source: this.countrySource,
				sourceLayer: this.countrySourceLayer,
			},
			{ hover }
		)
	}

	private setSelected(featureId: string, selected: boolean): void {
		this.map.setFeatureState(
			{
				id: featureId,
				source: this.countrySource,
				sourceLayer: this.countrySourceLayer,
			},
			{ selected }
		)
	}

	private politicFilters(): any[] {
		return [
			'all',
			['==', ['get', 'disputed'], 'false'],
			['any', ['==', 'all', ['get', 'worldview']], ['in', 'US', ['get', 'worldview']]],
		]
	}

	addSnow() {
		this.map.setSnow({
			'density': 1,
			'intensity': 1.0,
			'center-thinning': 0.1,
			'direction': [0, 50],
			'opacity': 1.0,
			'color': `#ffffff`,
			'flake-size': 0.71,
			'vignette': 1,
			'vignette-color': `#ffffff`,
		})
	}
}
